import { useState, useEffect } from "react";
import { Card, CardContent, Typography, Box, Slider, Select, MenuItem, Button, InputLabel, FormControl, Chip, IconButton, TextField, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SpeedIcon from '@mui/icons-material/Speed';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api, authenticatedFetch } from '../utils/api';

function QuickCheckinForm() {
  const [isQuickMode, setIsQuickMode] = useState(true);
  const [mood, setMood] = useState("Good");
  const [energy, setEnergy] = useState(7);
  const [sleep, setSleep] = useState(7);
  const [submitted, setSubmitted] = useState(false);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Detailed mode states (same as original form)
  const [soreness, setSoreness] = useState(3);
  const [sleepQuality, setSleepQuality] = useState("Good");
  const [stressLevel, setStressLevel] = useState(4);
  const [recovery, setRecovery] = useState("Mostly Recovered");
  const [hydration, setHydration] = useState(7);
  const [nutritionQuality, setNutritionQuality] = useState("Good");
  const [workoutMotivation, setWorkoutMotivation] = useState(7);
  const [fitnessGoal, setFitnessGoal] = useState("General Fitness");
  const [notes, setNotes] = useState("");
  const [weight, setWeight] = useState("");

  const navigate = useNavigate();

  // Check if user has already checked in today
  useEffect(() => {
    checkTodayCheckin();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkTodayCheckin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.checkinToday, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasCheckedInToday(data.hasCheckedInToday);
        setTodayCheckin(data.todayCheckin);
        
        // Pre-populate form with today's checkin data if it exists
        if (data.todayCheckin) {
          populateFormWithCheckin(data.todayCheckin);
        }
      }
    } catch (error) {
      console.error('Error checking today\'s checkin:', error);
    } finally {
      setLoading(false);
    }
  };

  const populateFormWithCheckin = (checkin) => {
    setMood(checkin.mood || "Good");
    setEnergy(checkin.energy || 7);
    setSleep(checkin.sleepHours || 7);
    setSoreness(checkin.soreness || 3);
    setSleepQuality(checkin.sleepQuality || "Good");
    setStressLevel(checkin.stressLevel || 4);
    setRecovery(checkin.recovery || "Mostly Recovered");
    setHydration(checkin.hydration || 7);
    setNutritionQuality(checkin.nutritionQuality || "Good");
    setWorkoutMotivation(checkin.workoutMotivation || 7);
    setFitnessGoal(checkin.fitnessGoal || "General Fitness");
    setNotes(checkin.notes || "");
    setWeight(checkin.weight ? checkin.weight.toString() : "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No authentication token found");
      return;
    }

    setIsUpdating(true);

    try {
      const submitData = isQuickMode ? {
        // Quick mode - only essential fields
        mood,
        energy,
        sleepHours: sleep,
        sleepQuality: "Good", // Default
        soreness: 3, // Default
        stressLevel: 5, // Default
        recovery: "Mostly Recovered", // Default
        hydration: 7, // Default
        nutritionQuality: "Good", // Default
        workoutMotivation: energy, // Use energy as proxy
        fitnessGoal: "General Fitness", // Default
        notes: hasCheckedInToday ? notes : `Quick check-in: ${mood} mood, ${energy}/10 energy, ${sleep}h sleep`
      } : {
        // Detailed mode - all fields
        mood, energy, soreness, sleepHours: sleep, sleepQuality, stressLevel, 
        recovery, hydration, nutritionQuality, workoutMotivation, fitnessGoal, 
        notes, weight: weight ? Number(weight) : undefined
      };

      const response = await fetch(api.checkin, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitted(true);
        setHasCheckedInToday(true);
        setTodayCheckin(result.checkIn);
        
        setTimeout(() => navigate('/'), 2000);
      } else {
        console.error("Failed to submit check-in");
      }
    } catch (err) {
      console.error("Error connecting to backend:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const getMoodEmoji = (mood) => {
    const emojis = { 'Excellent': '😄', 'Good': '🙂', 'Okay': '😐', 'Poor': '😔', 'Terrible': '😢' };
    return emojis[mood] || '😐';
  };

  const getEnergyColor = (energy) => {
    if (energy >= 8) return 'success';
    if (energy >= 6) return 'primary';
    if (energy >= 4) return 'warning';
    return 'error';
  };

  const handleStartEditing = () => {
    setSubmitted(false);
  };

  const handleRefreshToday = () => {
    checkTodayCheckin();
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" color="success.main" gutterBottom>
          {hasCheckedInToday ? 'Today\'s check-in updated!' : 'Check-in complete!'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Redirecting to dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      {/* Daily Check-in Status */}
      {hasCheckedInToday && !submitted && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={handleRefreshToday} title="Refresh">
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleStartEditing} title="Edit Today's Check-in">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="body2">
            <strong>You've already checked in today!</strong> You can update your check-in below.
            {todayCheckin && (
              <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                Last updated: {new Date(todayCheckin.timestamp).toLocaleTimeString()}
              </Box>
            )}
          </Typography>
        </Alert>
      )}

      {/* Mode Toggle */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                color={isQuickMode ? "primary" : "default"}
                onClick={() => setIsQuickMode(true)}
                sx={{ bgcolor: isQuickMode ? 'primary.50' : 'transparent' }}
              >
                <SpeedIcon />
              </IconButton>
              <IconButton 
                color={!isQuickMode ? "primary" : "default"}
                onClick={() => setIsQuickMode(false)}
                sx={{ bgcolor: !isQuickMode ? 'primary.50' : 'transparent' }}
              >
                <TuneIcon />
              </IconButton>
            </Box>
            <Box>
              <Chip 
                label={isQuickMode ? "Quick Mode" : "Detailed Mode"} 
                color={isQuickMode ? "success" : "primary"}
                variant="filled"
              />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isQuickMode ? "Quick 30-second check-in with just the essentials" : "Complete detailed health tracking"}
            {hasCheckedInToday && " (This will update today's check-in)"}
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isQuickMode ? <SpeedIcon /> : <TuneIcon />}
            {hasCheckedInToday ? 'Update Today\'s Check-in' : (isQuickMode ? 'Quick Health Check-in' : 'Detailed Health Check-in')}
          </Typography>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            
            {/* Quick Mode - Essential Fields Only */}
            {isQuickMode ? (
              <>
                {/* Mood */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getMoodEmoji(mood)} How are you feeling?
                  </Typography>
                  <FormControl fullWidth>
                    <Select value={mood} onChange={e => setMood(e.target.value)} required>
                      <MenuItem value="Excellent">😄 Excellent</MenuItem>
                      <MenuItem value="Good">🙂 Good</MenuItem>
                      <MenuItem value="Okay">😐 Okay</MenuItem>
                      <MenuItem value="Poor">😔 Poor</MenuItem>
                      <MenuItem value="Terrible">😢 Terrible</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Energy */}
                <Box>
                  <Typography variant="h6" gutterBottom>⚡ Energy Level: {energy}/10</Typography>
                  <Slider 
                    value={energy} 
                    min={1} 
                    max={10} 
                    step={1} 
                    onChange={(_, v) => setEnergy(v)} 
                    valueLabelDisplay="auto"
                    color={getEnergyColor(energy)}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
                    <span>Exhausted</span>
                    <span>Energized</span>
                  </Box>
                </Box>

                {/* Sleep */}
                <Box>
                  <Typography variant="h6" gutterBottom>😴 Sleep Last Night: {sleep} hours</Typography>
                  <Slider 
                    value={sleep} 
                    min={0} 
                    max={12} 
                    step={0.5} 
                    onChange={(_, v) => setSleep(v)} 
                    valueLabelDisplay="auto"
                    color={sleep >= 7 ? 'success' : sleep >= 5 ? 'warning' : 'error'}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
                    <span>No sleep</span>
                    <span>Great sleep</span>
                  </Box>
                </Box>

                <Button 
                  type="submit" 
                  variant="contained" 
                  color={hasCheckedInToday ? "primary" : "success"}
                  size="large" 
                  disabled={isUpdating}
                  sx={{ fontWeight: 600, py: 2, fontSize: '1.1rem' }}
                >
                  {isUpdating ? 'Saving...' : hasCheckedInToday ? '🔄 Update Check-in' : '⚡ Quick Submit (30s)'}
                </Button>
              </>
            ) : (
              <>
                {/* Detailed Mode - All Original Fields */}
                <Box>
                  <Typography variant="h6" color="secondary" gutterBottom>Core Wellness</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Mood</InputLabel>
                    <Select value={mood} label="Mood" onChange={e => setMood(e.target.value)} required>
                      <MenuItem value="Excellent">Excellent</MenuItem>
                      <MenuItem value="Good">Good</MenuItem>
                      <MenuItem value="Okay">Okay</MenuItem>
                      <MenuItem value="Poor">Poor</MenuItem>
                      <MenuItem value="Terrible">Terrible</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Typography gutterBottom>Energy Level: {energy}</Typography>
                  <Slider value={energy} min={1} max={10} step={1} onChange={(_, v) => setEnergy(v)} valueLabelDisplay="auto" sx={{ mb: 2 }} />
                  
                  <Typography gutterBottom>Soreness Level: {soreness}</Typography>
                  <Slider value={soreness} min={1} max={10} step={1} onChange={(_, v) => setSoreness(v)} valueLabelDisplay="auto" sx={{ mb: 2 }} />
                  
                  <Typography gutterBottom>Hours of Sleep: {sleep}</Typography>
                  <Slider value={sleep} min={0} max={24} step={1} onChange={(_, v) => setSleep(v)} valueLabelDisplay="auto" sx={{ mb: 2 }} />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Sleep Quality</InputLabel>
                    <Select value={sleepQuality} label="Sleep Quality" onChange={e => setSleepQuality(e.target.value)}>
                      <MenuItem value="Excellent">Excellent</MenuItem>
                      <MenuItem value="Good">Good</MenuItem>
                      <MenuItem value="Fair">Fair</MenuItem>
                      <MenuItem value="Poor">Poor</MenuItem>
                      <MenuItem value="Terrible">Terrible</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Stress Level */}
                <Box>
                  <Typography variant="h6" color="secondary" gutterBottom>Stress & Recovery</Typography>
                  <Typography gutterBottom>Stress Level: {stressLevel}</Typography>
                  <Slider value={stressLevel} min={1} max={10} step={1} onChange={(_, v) => setStressLevel(v)} valueLabelDisplay="auto" sx={{ mb: 2 }} />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Recovery Level</InputLabel>
                    <Select value={recovery} label="Recovery Level" onChange={e => setRecovery(e.target.value)}>
                      <MenuItem value="Fully Recovered">Fully Recovered</MenuItem>
                      <MenuItem value="Mostly Recovered">Mostly Recovered</MenuItem>
                      <MenuItem value="Somewhat Recovered">Somewhat Recovered</MenuItem>
                      <MenuItem value="Not Recovered">Not Recovered</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Hydration & Nutrition */}
                <Box>
                  <Typography variant="h6" color="secondary" gutterBottom>Hydration & Nutrition</Typography>
                  <Typography gutterBottom>Hydration Level: {hydration}</Typography>
                  <Slider value={hydration} min={1} max={10} step={1} onChange={(_, v) => setHydration(v)} valueLabelDisplay="auto" sx={{ mb: 2 }} />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Nutrition Quality</InputLabel>
                    <Select value={nutritionQuality} label="Nutrition Quality" onChange={e => setNutritionQuality(e.target.value)}>
                      <MenuItem value="Excellent">Excellent</MenuItem>
                      <MenuItem value="Good">Good</MenuItem>
                      <MenuItem value="Fair">Fair</MenuItem>
                      <MenuItem value="Poor">Poor</MenuItem>
                      <MenuItem value="Terrible">Terrible</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Workout & Goals */}
                <Box>
                  <Typography variant="h6" color="secondary" gutterBottom>Workout & Goals</Typography>
                  <Typography gutterBottom>Workout Motivation: {workoutMotivation}</Typography>
                  <Slider value={workoutMotivation} min={1} max={10} step={1} onChange={(_, v) => setWorkoutMotivation(v)} valueLabelDisplay="auto" sx={{ mb: 2 }} />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Fitness Goal</InputLabel>
                    <Select value={fitnessGoal} label="Fitness Goal" onChange={e => setFitnessGoal(e.target.value)}>
                      <MenuItem value="Weight Loss">Weight Loss</MenuItem>
                      <MenuItem value="Muscle Gain">Muscle Gain</MenuItem>
                      <MenuItem value="General Fitness">General Fitness</MenuItem>
                      <MenuItem value="Endurance">Endurance</MenuItem>
                      <MenuItem value="Strength">Strength</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Weight & Notes */}
                <Box>
                  <Typography variant="h6" color="secondary" gutterBottom>Additional Info</Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Weight (lbs) - Optional"
                    value={weight}
                    onChange={e => setWeight(e.target.value)}
                    placeholder="Enter weight"
                    sx={{ mb: 2 }}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes - Optional"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Any additional thoughts or observations..."
                  />
                </Box>

                <Button 
                  type="submit" 
                  variant="contained" 
                  color={hasCheckedInToday ? "primary" : "success"}
                  size="large" 
                  disabled={isUpdating}
                  sx={{ fontWeight: 600, py: 2 }}
                >
                  {isUpdating ? 'Saving...' : hasCheckedInToday ? 'Update Today\'s Check-In' : 'Submit Detailed Check-In'}
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default QuickCheckinForm;

